import { faBed } from '@fortawesome/pro-regular-svg-icons/faBed';
import { faTag } from '@fortawesome/pro-regular-svg-icons/faTag';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import {
  AUCTION_CATEGORIES,
  AUCTION_SORT_ORDER_OPTIONS,
  CURRENCY_OPTIONS,
  EXPAND_OPTIONS,
  HEATING_OPTIONS,
  PRICE_OPTIONS,
  RADIUS_OPTIONS,
  RENTAL_PRICE_OPTIONS,
  SORT_ORDER_OPTIONS,
  YEAR_BUILT_OPTIONS,
} from '@propertypal/shared/src/constants/filterOptions';
import { laptopMax } from '@propertypal/shared/src/constants/mediaQueries';
import { CLOSE_TO_SEARCH } from '@propertypal/shared/src/constants/search';
import { RecentSearch } from '@propertypal/shared/src/hooks/useRecentSearches';
import { UseSearchState } from '@propertypal/shared/src/hooks/useSearchState';
import { initialState, loadSavedSearch, setSearch } from '@propertypal/shared/src/reducers/search';
import { useAppDispatch } from '@propertypal/shared/src/reducers/store';
import FilterIcon from '@propertypal/shared/src/resources/icons/search/filter-icon-black.svg';
import CloseToPin from '@propertypal/shared/src/resources/icons/search/pin.svg';
import currencyDirect from '@propertypal/shared/src/resources/logos/currencies-direct-logo-dark.png';
import { trackGaEvent, trackGaLink } from '@propertypal/shared/src/services/analytics';
import { PropertySearchResult } from '@propertypal/shared/src/types/propertySearch';
import { getCurrencySymbol } from '@propertypal/shared/src/utils/floatToCurrency';
import { usePropertyStyles } from '@propertypal/shared/src/utils/property/getPropertyStyles';
import { getCategoryOption, getPriceSuffix } from '@propertypal/shared/src/utils/search/category';
import { getTotalFilters } from '@propertypal/shared/src/utils/search/getFilterTitles';
import { Filters } from '@propertypal/shared/src/utils/search/getNlpFilters';
import { Suggestion } from '@propertypal/shared/src/utils/search/groupTokensByType';
import Button from '@propertypal/web-ui/src/buttons/Button';
import DropButton from '@propertypal/web-ui/src/buttons/DropButton';
import useCloseToSearch from '@propertypal/web-ui/src/close-search/useCloseToSearch';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import Checkbox from '@propertypal/web-ui/src/inputs/Checkbox';
import CheckItem from '@propertypal/web-ui/src/inputs/CheckItem';
import SearchInput from '@propertypal/web-ui/src/inputs/SearchInput';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import TagInput from '@propertypal/web-ui/src/inputs/TagInput';
import Toggle from '@propertypal/web-ui/src/inputs/Toggle';
import { Row } from '@propertypal/web-ui/src/layout';
import useMediaQuery from '@propertypal/web-ui/src/media-query/useMediaQuery';
import CloseToSearchModal from '@propertypal/web-ui/src/modal/CloseToSearchModal.pn';
import { Text } from '@propertypal/web-ui/src/typography';
import { useRouter } from 'next/router';
import React, { FormEvent, FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import { getPropertyStyleIcon } from '../../../constants/styleIcons';
import getCurrentLocation from '../../../services/location';
import togglePageScroll from '../../../utils/togglePageScroll';
import {
  BedsDropdown,
  BroadbandTooltip,
  CloseToBadge,
  CloseToEditText,
  CloseToInput,
  CloseToSearchText,
  Container,
  Content,
  DropdownWrapper,
  EpcArrow,
  FilterPane,
  FilterPaneBottom,
  FilterPaneContent,
  FilterPaneTitle,
  PriceDropdown,
  SearchInputWrapper,
  StyleBox,
  StylesGrid,
  TitleRow,
  PartnershipBar,
  CurrencyColumn,
} from './SearchBar.style.pn';

const MIN_BED_OPTIONS = (maxBeds: number) => [
  {
    label: 'Any',
    value: '',
  },
  {
    label: '1',
    value: '1',
  },
  {
    label: '2',
    value: '2',
    disabled: maxBeds < 2,
  },
  {
    label: '3',
    value: '3',
    disabled: maxBeds < 3,
  },
  {
    label: '4',
    value: '4',
    disabled: maxBeds < 4,
  },
  {
    label: '5',
    value: '5',
    disabled: maxBeds < 5,
  },
  {
    label: '6',
    value: '6',
    disabled: maxBeds < 6,
  },
];

const MAX_BED_OPTIONS = (minBeds: number) => [
  {
    label: 'Any',
    value: '',
  },
  {
    label: '1',
    value: '1',
    disabled: minBeds > 1,
  },
  {
    label: '2',
    value: '2',
    disabled: minBeds > 2,
  },
  {
    label: '3',
    value: '3',
    disabled: minBeds > 3,
  },
  {
    label: '4',
    value: '4',
    disabled: minBeds > 4,
  },
  {
    label: '5',
    value: '5',
    disabled: minBeds > 5,
  },
  {
    label: '6',
    value: '6',
    disabled: minBeds > 6,
  },
];

export const CATEGORY_OPTIONS = [
  { label: 'For Sale', value: 'residentialForSale' },
  { label: 'For Rent', value: 'residentialForRent' },
  { label: 'Student Rentals', value: 'studentForRent' },
  { label: 'New Homes', value: 'newhomesForSale' },
  { label: 'Holiday Homes', value: 'holidayhomesForRent' },
  { label: 'Residential Auctions', value: 'residentialAuctionsForSale' },
  { label: 'Commercial Auctions', value: 'commercialAuctionsForSale' },
  { label: 'Land & Sites For Sale', value: 'landForSale' },
  { label: 'Land & Sites For Rent', value: 'landForRent' },
  { label: 'Commercial For Sale', value: 'commercialForSale' },
  { label: 'Commercial For Rent', value: 'commercialForRent' },
  { label: 'Agricultural', value: 'agriculturalForSale' },
  { label: 'Agricultural Rentals', value: 'agriculturalForRent' },
];

export const EPC_OPTIONS = [
  { label: 'Any', value: '', color: 'linear-gradient(to right, #E11524 0%, #E87B18 50%, #F3C401 100%)' },
  { label: 'D+', ariaLabel: 'D and above', value: 'D', color: '#f9f503', textColor: '#353535' },
  { label: 'C+', ariaLabel: 'C and above', value: 'C', color: '#a0d20f', textColor: '#353535' },
  { label: 'B+', ariaLabel: 'B and above', value: 'B', color: '#33a028' },
  { label: 'A', ariaLabel: 'A', value: 'A', color: '#267f2d' },
];

export const FLOOR_SIZE_OPTIONS = [
  { label: 'Any', value: '' },
  { label: '500+', ariaLabel: '500+ Sq ft', value: '46.5' },
  { label: '750+', ariaLabel: '750+ Sq ft', value: '70' },
  { label: '1,000+', ariaLabel: '1,000+ Sq ft', value: '93' },
  { label: '1,500+', ariaLabel: '1,500+ Sq ft', value: '139' },
  { label: '2,000+', ariaLabel: '2,000+ Sq ft', value: '186' },
];

interface Props {
  onSearchModalToggle: (open: boolean) => void;
  loading: boolean;
  imageLocator: string;
  searchState: UseSearchState;
  onSubmit: () => void;
  onDrawSearchClick?: () => void;
  onTravelTimeClick: () => void;
  onCloseToSearch: (polyIds: number[]) => Promise<boolean>;
  recentSearches: RecentSearch[];
  removeRecentSearch: (search: RecentSearch) => void;
  loggedIn: boolean;
  importantPlaces?: PropertySearchResult['importantPlaces'];
}

const formatPrice = (price: string, currencySymbol: string) => {
  if (!price) return '';

  const priceInt = parseInt(price, 10);

  if (priceInt >= 1000000) return `${currencySymbol}${priceInt / 1000000}m`;
  return `${currencySymbol}${priceInt / 1000}k`;
};

const getBedLabel = (minBeds: string, maxBeds: string) => {
  if (minBeds && maxBeds) return `${minBeds} - ${maxBeds}`;
  if (minBeds) return `Min ${minBeds}`;
  if (maxBeds) return `Max ${maxBeds}`;

  return 'Beds';
};

const getPriceLabel = (minPrice: string, maxPrice: string, currencySymbol: string) => {
  const minPriceLabel = formatPrice(minPrice, currencySymbol);
  const maxPriceLabel = formatPrice(maxPrice, currencySymbol);

  if (minPriceLabel && maxPriceLabel) return `${minPriceLabel} - ${maxPriceLabel}`;
  if (minPriceLabel) return `Min ${minPriceLabel}`;
  if (maxPriceLabel) return `Max ${maxPriceLabel}`;

  return 'Price';
};

const SearchBar: FunctionComponent<Props> = (props) => {
  const {
    searchText,
    appFilters,
    nlpData,
    nlpFilters,
    handleChangeText,
    handleFilterChange,
    handleStyleChange,
    handleClearFilters,
    getLocation,
    fetchingLocation,
    showRadius,
    showExpand,
  } = props.searchState;
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const asyncDispatch = useAppDispatch();
  const theme = useTheme();
  const router = useRouter();
  const filters = { ...appFilters, ...nlpFilters };
  const isRental = filters.saleType === 'rent';
  const currencySymbol = getCurrencySymbol(filters.currency);
  const [minPriceSuffix, fullMinPriceSuffix] = getPriceSuffix(filters, 'min', 'minimum');
  const [maxPriceSuffix, fullMaxPriceSuffix] = getPriceSuffix(filters, 'max', 'maximum');
  const [filterPane, setFilterPane] = useState('');
  const propertyStyles = usePropertyStyles(filters.category, filters.saleType);
  const totalFilters = getTotalFilters(appFilters, true);
  const isTablet = useMediaQuery({ query: `(${laptopMax})` });
  const [showBroadbandTooltip, setShowBroadbandTooltip] = useState<boolean>(false);
  const { closeToModal, handleCloseToClick, onCloseCloseToModal } = useCloseToSearch({
    page: 'SRP',
    router,
    loggedIn: props.loggedIn,
  });

  const minPriceOptions = isRental
    ? RENTAL_PRICE_OPTIONS(currencySymbol, minPriceSuffix, fullMinPriceSuffix)
    : PRICE_OPTIONS(currencySymbol, minPriceSuffix, fullMinPriceSuffix);
  const maxPriceOptions = isRental
    ? RENTAL_PRICE_OPTIONS(currencySymbol, maxPriceSuffix, fullMaxPriceSuffix)
    : PRICE_OPTIONS(currencySymbol, maxPriceSuffix, fullMaxPriceSuffix);

  const isCloseToSearch = useMemo(
    () => searchText === CLOSE_TO_SEARCH && !!props.importantPlaces?.length && !!filters?.polyIds?.length,
    [searchText, props.importantPlaces, filters?.polyIds],
  );

  const toggleFilterPane = (pane: string) => () => {
    setFilterPane(pane === filterPane ? '' : pane);
  };

  const handleSuggestionClick = (token: Suggestion) => {
    if (token.searchParams) {
      asyncDispatch(loadSavedSearch(token.searchParams, props.imageLocator));
    } else if (token.url) {
      router.push(token.url);
    } else {
      handleChangeText(token.value);
    }
  };

  const handleRecentSearch = (text: string, recentFilters: Filters) => {
    trackGaEvent('recent_search_click', { location: 'Search Bar' });

    dispatch(
      setSearch({
        text,
        filters: { ...initialState.filters, ...recentFilters },
      }),
    );
    props.onSubmit();
  };

  const handleDrawSearch = (position: 'suggestion' | 'inline') => {
    if (props.onDrawSearchClick) {
      trackGaEvent('draw_click', {
        page: 'SRP',
        location: 'Search Box',
        position,
      });
      props.onDrawSearchClick();
    }
  };

  const handleSubmit = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    inputRef.current?.blur();
    props.onSearchModalToggle(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const latLng = await getLocation(getCurrentLocation, false);

    setFilterPane('');
    dispatch(
      setSearch({
        text: searchText,
        filters: { ...appFilters, ...latLng },
      }),
    );
    props.onSubmit();
  };

  const closeToSearchText = useMemo(() => {
    if (isCloseToSearch) {
      let searchTxt = searchText;
      const ip = [
        ...props
          .importantPlaces!.filter((p) => !!filters?.polyIds?.some((id) => p.polygonIds.includes(id)))
          .map((p) => p.name),
      ];
      if (ip.length === 0) searchTxt = searchText;
      else if (ip.length === 1) searchTxt = ip?.[0] || '';
      else if (ip.length === 2) searchTxt = `${ip[0]} & ${ip[1]}`;
      else {
        const lastItem = ip.pop();
        searchTxt = `${ip.join(', ')} & ${lastItem}`;
      }

      return searchTxt;
    }
    return '';
  }, [isCloseToSearch, searchText, props.importantPlaces, filters?.polyIds]);

  useEffect(() => {
    togglePageScroll('#filterPaneContent', !isTablet || filterPane !== 'filters');
  }, [filterPane, isTablet]);

  useEffect(() => {
    if (filterPane === 'filters' || filterPane === 'price') {
      trackGaEvent('currency_direct_logo_impression', {
        location: 'SRP',
      });
    }
  }, [filterPane]);

  return (
    <Container onSubmit={handleSubmit}>
      <Content>
        <SearchInputWrapper>
          {isCloseToSearch ? (
            <CloseToInput type="button" data-testid="close-to-search-input" onClick={() => handleCloseToClick()}>
              <CloseToBadge>
                <CloseToPin />
                <span>Close To</span>
              </CloseToBadge>
              <CloseToSearchText
                aria-label={closeToSearchText}
                data-testid="close-to-search-text"
                value={closeToSearchText}
                disabled
              />
              <CloseToEditText>Edit</CloseToEditText>
            </CloseToInput>
          ) : (
            <SearchInput
              inputRef={inputRef}
              onChangeText={handleChangeText}
              value={searchText}
              recentSearches={props.recentSearches}
              onRemoveRecentSearch={props.removeRecentSearch}
              onRecentSearchClick={handleRecentSearch}
              nlpData={nlpData}
              loading={fetchingLocation}
              onSuggestionClick={handleSuggestionClick}
              onLocationClick={(position) => {
                trackGaEvent('current_location_click', {
                  page: 'SRP',
                  location: 'Search Box',
                  position,
                });
                getLocation(getCurrentLocation, true);
              }}
              onTravelTimeClick={(position) => {
                trackGaEvent('travel_time_click', {
                  page: 'SRP',
                  location: 'Search Box',
                  position,
                });
                props.onTravelTimeClick();
              }}
              onDrawSearchClick={props.onDrawSearchClick ? handleDrawSearch : undefined}
              onCloseToClick={handleCloseToClick}
              showMobileSearchIcon
            />
          )}
        </SearchInputWrapper>

        {showRadius && !isTablet && (
          <SelectInput
            ariaLabel="Select a search radius"
            containerStyle={{ maxWidth: 175 }}
            id="selectRadius"
            placeholder="Radius"
            options={RADIUS_OPTIONS}
            value={filters.radius}
            onChange={handleFilterChange('radius')}
            testID="selectRadius"
          />
        )}

        {showExpand && !isTablet && (
          <SelectInput
            ariaLabel="Expand search radius"
            containerStyle={{ maxWidth: 175 }}
            id="expandRadius"
            disableMaxHeight
            placeholder="Expand"
            options={EXPAND_OPTIONS}
            value={filters.expand}
            onChange={handleFilterChange('expand')}
            testID="expandRadius"
          />
        )}

        <DropdownWrapper $minWidth={118}>
          <DropButton
            icon={faTag}
            text={getPriceLabel(filters.minPrice, filters.maxPrice, currencySymbol)}
            active={filterPane === 'price'}
            onClick={toggleFilterPane('price')}
            testID="priceDropdown"
          />

          {filterPane === 'price' && (
            <PriceDropdown>
              <CurrencyColumn>
                <Toggle
                  label="Currency"
                  options={CURRENCY_OPTIONS}
                  onChange={handleFilterChange('currency')}
                  value={filters.currency || 'GBP'}
                  contentStyle={{ height: 50 }}
                />

                <PartnershipBar
                  href="/currency-transfers"
                  onClick={trackGaLink('currencies_direct_logo_click', 'Price Dropdown', 'SRP')}
                >
                  In partnership with <img src={currencyDirect.src} alt="Currencies Direct" />
                </PartnershipBar>
              </CurrencyColumn>

              <div style={{ flex: 1 }}>
                <Row gap={15} mb={15}>
                  <SelectInput
                    ariaLabel="Select Min Price"
                    id="selectMinPrice"
                    label="Min Price"
                    options={minPriceOptions}
                    value={filters.minPrice}
                    onChange={handleFilterChange('minPrice')}
                    testID="selectMinPrice"
                  />

                  <SelectInput
                    ariaLabel="Select Max Price"
                    id="selectMaxPrice"
                    label="Max Price"
                    options={maxPriceOptions}
                    value={filters.maxPrice}
                    onChange={handleFilterChange('maxPrice')}
                    testID="selectMaxPrice"
                  />
                </Row>

                <Button containerStyle={{ height: 54 }} onClick={toggleFilterPane('')}>
                  Done
                </Button>
              </div>
            </PriceDropdown>
          )}
        </DropdownWrapper>

        <DropdownWrapper $minWidth={102}>
          <DropButton
            icon={faBed}
            text={getBedLabel(filters.minBeds, filters.maxBeds)}
            active={filterPane === 'beds'}
            onClick={toggleFilterPane('beds')}
            testID="bedsDropdown"
          />

          {filterPane === 'beds' && (
            <BedsDropdown>
              <TagInput
                label="Min Bedrooms"
                options={MIN_BED_OPTIONS(parseInt(filters.maxBeds, 10))}
                value={filters.minBeds}
                onOptionChange={handleFilterChange('minBeds')}
                testID="minBedOptions"
              />

              <TagInput
                label="Max Bedrooms"
                options={MAX_BED_OPTIONS(parseInt(filters.minBeds, 10))}
                value={filters.maxBeds}
                onOptionChange={handleFilterChange('maxBeds')}
                testID="maxBedOptions"
              />
            </BedsDropdown>
          )}
        </DropdownWrapper>

        <DropdownWrapper $minWidth={118}>
          <DropButton
            icon={() => <FilterIcon />}
            containerStyle={{ boxShadow: `inset 0 0 0 2px ${theme.textDark}` }}
            text="Filter & Sort"
            active={filterPane === 'filters'}
            onClick={toggleFilterPane('filters')}
            total={totalFilters}
            testID="filtersDropdown"
          />
        </DropdownWrapper>

        <Button
          containerStyle={{ flex: '0 0 160px' }}
          loading={props.loading}
          testID="searchSubmitButton"
          type="submit"
        >
          Search
        </Button>

        <FilterPane style={{ display: filterPane === 'filters' ? 'block' : 'none' }}>
          <FilterPaneContent id="filterPaneContent">
            <FilterPaneTitle>
              <h2>Refine your Search</h2>

              <button onClick={() => setFilterPane('')} type="button">
                <FontAwesomeIcon icon={faTimes} size={24} color={theme.textDark} />
              </button>
            </FilterPaneTitle>

            {showRadius && isTablet && (
              <SelectInput
                ariaLabel="Select a search radius"
                containerStyle={{ marginBottom: 15 }}
                id="selectRadius"
                placeholder="Radius"
                label="Radius"
                options={RADIUS_OPTIONS}
                value={filters.radius}
                onChange={handleFilterChange('radius')}
                testID="selectRadius"
              />
            )}

            {showExpand && isTablet && (
              <SelectInput
                ariaLabel="Expand search radius"
                containerStyle={{ marginBottom: 15 }}
                id="expandRadius"
                disableMaxHeight
                placeholder="Expand"
                label="Expand"
                options={EXPAND_OPTIONS}
                value={filters.expand}
                onChange={handleFilterChange('expand')}
                testID="expandRadius"
              />
            )}

            <Row gap={15} mb={15}>
              <SelectInput
                ariaLabel="Sort By"
                id="selectSort"
                label="Sort By"
                options={
                  AUCTION_CATEGORIES.includes(filters.category) ? AUCTION_SORT_ORDER_OPTIONS : SORT_ORDER_OPTIONS
                }
                value={filters.sortOrder}
                onChange={handleFilterChange('sortOrder')}
              />

              <SelectInput
                ariaLabel="Status"
                id="selectStatus"
                label="Status"
                options={CATEGORY_OPTIONS}
                value={getCategoryOption(filters)}
                onChange={handleFilterChange('category')}
              />
            </Row>

            <Row gap={15} mb={15} alignItems="flex-end">
              <Toggle
                label="Currency"
                options={CURRENCY_OPTIONS}
                onChange={handleFilterChange('currency')}
                value={filters.currency || 'GBP'}
                contentStyle={{ height: 50 }}
                containerStyle={{ maxWidth: 200 }}
              />

              <PartnershipBar
                href="/currency-transfers"
                onClick={trackGaLink('currencies_direct_logo_click', 'Price Dropdown', 'SRP')}
              >
                In partnership with <img src={currencyDirect.src} alt="Currencies Direct" />
              </PartnershipBar>
            </Row>

            <Row gap={15} mb={15}>
              <SelectInput
                ariaLabel="Select Min Price"
                id="selectMinPrice"
                label="Min Price"
                options={minPriceOptions}
                value={filters.minPrice}
                onChange={handleFilterChange('minPrice')}
              />

              <SelectInput
                ariaLabel="Select Max Price"
                id="selectMaxPrice"
                label="Max Price"
                options={maxPriceOptions}
                value={filters.maxPrice}
                onChange={handleFilterChange('maxPrice')}
              />
            </Row>

            <TagInput
              label="Min Bedrooms"
              options={MIN_BED_OPTIONS(parseInt(filters.maxBeds, 10))}
              value={filters.minBeds}
              onOptionChange={handleFilterChange('minBeds')}
            />

            <TagInput
              label="Max Bedrooms"
              options={MAX_BED_OPTIONS(parseInt(filters.minBeds, 10))}
              value={filters.maxBeds}
              onOptionChange={handleFilterChange('maxBeds')}
            />

            {propertyStyles && (
              <>
                <Text fontWeight="600" mt={30} mb={15}>
                  Property Types
                </Text>
                <StylesGrid>
                  {propertyStyles.map((style) => (
                    <StyleBox key={style.key}>
                      <img
                        {...getPropertyStyleIcon(filters.category, style.key, filters.styles.includes(style.key))}
                        alt="style icon"
                      />
                      <CheckItem
                        checked={filters.styles.includes(style.key)}
                        label={style.text}
                        // checkedIcon={getPropertyStyleIcon(filters.category, style.key, true)}
                        // unCheckedIcon={getPropertyStyleIcon(filters.category, style.key, false)}
                        onValueChange={handleStyleChange(style.key)}
                        testID={`checkItem-${style.key}`}
                      />
                    </StyleBox>
                  ))}
                </StylesGrid>
              </>
            )}

            <TagInput
              label="Total Floor Area -sq ft"
              value={filters.minInternalArea}
              options={FLOOR_SIZE_OPTIONS}
              onOptionChange={handleFilterChange('minInternalArea')}
            />

            <TagInput
              label="Year Built"
              value={filters.yearBuilt}
              options={YEAR_BUILT_OPTIONS}
              onOptionChange={handleFilterChange('yearBuilt')}
            />

            <TagInput
              label="Heating Type"
              value={filters.heatingType}
              options={HEATING_OPTIONS}
              onOptionChange={handleFilterChange('heatingType')}
              multiSelect
            />

            <TagInput
              label="Energy Efficiency"
              value={filters.epc}
              options={EPC_OPTIONS}
              onOptionChange={handleFilterChange('epc')}
            />

            <EpcArrow />
            <Text style={{ maxWidth: 365 }} color={theme.backgroundMid} align="center">
              Lower running costs
            </Text>

            <Checkbox
              containerStyle={{ marginTop: 10 }}
              name="epcRatingType"
              value={filters.includePotentialEpc}
              onValueChange={handleFilterChange('includePotentialEpc')}
              disabled={!filters.epc}
              label={<Text>Include Potential EPC Ratings</Text>}
            />

            <Text style={{ maxWidth: 365, paddingLeft: 40 }} color={theme.backgroundMid} fontSize={14} mb={30}>
              This will include properties that, with improvements, could correspond with your choice above.
            </Text>

            <TitleRow>
              <Text fontWeight="600">Broadband Speed Availability</Text>

              <button
                style={{ position: 'relative' }}
                onMouseEnter={() => setShowBroadbandTooltip(true)}
                onMouseDown={() => setShowBroadbandTooltip(true)}
                onMouseLeave={() => setShowBroadbandTooltip(false)}
                type="button"
              >
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  color={theme.textDark}
                  style={{ fontSize: 14, display: 'flex', margin: '5px' }}
                />

                {showBroadbandTooltip && (
                  <BroadbandTooltip>
                    <p>
                      The speeds indicated on our checker represent the maximum estimated fixed-line speeds as predicted
                      by the network operators providing services in a particular area. Please note that these are
                      estimates, and actual service availability and speeds received at a given property may differ.
                      Besides fixed-line services, options for 4G, 5G, or satellite broadband may be available at the
                      address, contingent upon the necessary hardware. These alternatives are not reflected in the
                      checker&apos;s estimates.
                    </p>
                  </BroadbandTooltip>
                )}
              </button>
            </TitleRow>

            <Row mb={30} gap={15}>
              <CheckItem
                label="Any"
                checked={filters.broadband === ''}
                onValueChange={() => handleFilterChange('broadband')('')}
              />

              <CheckItem
                label="Superfast"
                subLabel="30 Mbps+"
                checked={filters.broadband === 'SUPERFAST'}
                onValueChange={() => handleFilterChange('broadband')('SUPERFAST')}
              />

              <CheckItem
                label="Ultrafast"
                subLabel="300 Mbps+"
                checked={filters.broadband === 'ULTRAFAST'}
                onValueChange={() => handleFilterChange('broadband')('ULTRAFAST')}
              />

              <CheckItem
                label="Ultrafast"
                subLabel="900 Mbps+"
                checked={filters.broadband === 'ULTRAFAST900'}
                onValueChange={() => handleFilterChange('broadband')('ULTRAFAST900')}
              />
            </Row>

            <Text fontWeight="600" mb={15}>
              Indoor
            </Text>

            <Checkbox
              name="Ensuite"
              label="Ensuite"
              value={filters.tags ? filters.tags.includes('ensuite') : false}
              onValueChange={handleFilterChange('tags-ensuite')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Utility Room"
              label="Utility Room"
              value={filters.tags ? filters.tags.includes('utilityRoom') : false}
              onValueChange={handleFilterChange('tags-utilityRoom')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Downstairs WC/Toliet"
              label="Downstairs WC/Toliet"
              value={filters.tags ? filters.tags.includes('downstairsToilet') : false}
              onValueChange={handleFilterChange('tags-downstairsToilet')}
              containerStyle={{ marginBottom: 20 }}
            />

            <Text fontWeight="600" mb={15}>
              Garage
            </Text>

            <Checkbox
              name="Garage"
              label="Garage"
              value={filters.garage}
              onValueChange={handleFilterChange('garage')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Large Garage"
              label="Large Garage"
              value={filters.largeGarage}
              onValueChange={handleFilterChange('largeGarage')}
              containerStyle={{ marginBottom: 20 }}
            />

            <Text fontWeight="600" mb={15}>
              Outdoor
            </Text>

            <Checkbox
              name="Garden"
              label="Garden"
              value={filters.tags ? filters.tags.includes('garden') : false}
              onValueChange={handleFilterChange('tags-garden')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Patio"
              label="Patio"
              value={filters.tags ? filters.tags.includes('patio') : false}
              onValueChange={handleFilterChange('tags-patio')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Paddock or Stables"
              label="Paddock or Stables"
              value={filters.tags ? filters.tags.includes('roomForAHorse') : false}
              onValueChange={handleFilterChange('tags-roomForAHorse')}
              containerStyle={{ marginBottom: 20 }}
            />

            <Text fontWeight="600" mb={15}>
              Parking
            </Text>

            <Checkbox
              name="Driveway"
              label="Driveway/Parking Space"
              value={filters.tags ? filters.tags.includes('driveway') : false}
              onValueChange={handleFilterChange('tags-driveway')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Off Street Parking"
              label="Off Street Parking"
              value={filters.tags ? filters.tags.includes('offStreetParking') : false}
              onValueChange={handleFilterChange('tags-offStreetParking')}
              containerStyle={{ marginBottom: 20 }}
            />

            <Text fontWeight="600" mb={15}>
              Other
            </Text>

            <Checkbox
              name="Cul-de-sac"
              label="Cul-de-sac"
              value={filters.tags ? filters.tags.includes('culDeSac') : false}
              onValueChange={handleFilterChange('tags-culDeSac')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Wheelchair Accessible"
              label="Wheelchair Accessible"
              value={filters.tags ? filters.tags.includes('disabledAccess') : false}
              onValueChange={handleFilterChange('tags-disabledAccess')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Sea Views"
              label="Sea Views"
              value={filters.tags ? filters.tags.includes('seaViews') : false}
              onValueChange={handleFilterChange('tags-seaViews')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Solar Panels"
              label="Solar Panels"
              value={filters.tags ? filters.tags.includes('solarPanels') : false}
              onValueChange={handleFilterChange('tags-solarPanels')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Swimming Pool"
              label="Swimming Pool"
              value={filters.tags ? filters.tags.includes('swimmingPool') : false}
              onValueChange={handleFilterChange('tags-swimmingPool')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Cash Sale"
              label="Cash Sale"
              value={filters.tags ? filters.tags.includes('cashSale') : false}
              onValueChange={handleFilterChange('tags-cashSale')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Rural Location"
              label="Rural Location"
              value={filters.tags ? filters.tags.includes('rural') : false}
              onValueChange={handleFilterChange('tags-rural')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Annex"
              label="With Annex"
              value={filters.tags ? filters.tags.includes('annex') : false}
              onValueChange={handleFilterChange('tags-annex')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Underfloor Heating"
              label="Underfloor Heating"
              value={filters.tags ? filters.tags.includes('underfloorHeating') : false}
              onValueChange={handleFilterChange('tags-underfloorHeating')}
              containerStyle={{ marginBottom: 20 }}
            />

            <Text fontWeight="600" mb={15}>
              Condition
            </Text>

            <Checkbox
              name="Recently Refurbished"
              label="Recently Refurbished or Renovated"
              value={filters.tags ? filters.tags.includes('recentlyRefurbished') : false}
              onValueChange={handleFilterChange('tags-recentlyRefurbished')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Listed Building"
              label="Listed Building"
              value={filters.tags ? filters.tags.includes('listedBuilding') : false}
              onValueChange={handleFilterChange('tags-listedBuilding')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="Investment Potenial"
              label="Investment Potenial"
              value={filters.tags ? filters.tags.includes('investmentPotential') : false}
              onValueChange={handleFilterChange('tags-investmentPotential')}
              containerStyle={{ marginBottom: 10 }}
            />

            <Checkbox
              name="In Need of Modernisation"
              label="In Need of Modernisation"
              value={filters.tags ? filters.tags.includes('includeRefurbishmentOportunity') : false}
              onValueChange={handleFilterChange('tags-includeRefurbishmentOportunity')}
              containerStyle={{ marginBottom: 20 }}
            />

            <Text fontWeight="600" mb={15}>
              Listing
            </Text>

            <Checkbox
              name="includeAgreed"
              label={`Include ${isRental ? 'Let' : 'Sale'} Agreed`}
              value={filters.includeAgreed}
              onValueChange={handleFilterChange('includeAgreed')}
              containerStyle={{ marginBottom: 10 }}
              testID="includeAgreed"
            />

            <Checkbox
              name="excludePoa"
              label="Exclude Price Not Provided"
              value={filters.excludePoa}
              onValueChange={handleFilterChange('excludePoa')}
              containerStyle={{ marginBottom: 10 }}
            />

            {filters.category === 'residential' && filters.saleType === 'rent' && (
              <Checkbox
                name="excludeStudentLettings"
                label="Exclude Student Lettings"
                value={filters.excludeStudentLettings}
                onValueChange={handleFilterChange('excludeStudentLettings')}
                containerStyle={{ marginBottom: 10 }}
                testID="excludeStudentLettings"
              />
            )}

            {['residential', 'newhomes', 'student', 'holidayhomes'].includes(filters.category) &&
              filters.saleType === 'sale' && (
                <Checkbox
                  name="chainFree"
                  label="Chain Free Properties Only"
                  value={!!filters.tags?.includes('chainFree')}
                  onValueChange={handleFilterChange('tags-chainFree')}
                  containerStyle={{ marginBottom: 10 }}
                />
              )}

            {((filters.category === 'residential' && filters.saleType === 'sale') ||
              filters.category === 'newhomes') && (
              <Checkbox
                name="suitableForCoOwnership"
                label="Suitable for Co-Ownership"
                value={filters.coOwnership}
                onValueChange={handleFilterChange('coOwnership')}
                containerStyle={{ marginBottom: 10 }}
              />
            )}

            {filters.category === 'residential' && filters.saleType === 'sale' && (
              <Checkbox
                name="excludeNewHomes"
                label="Exclude New Homes"
                value={filters.excludeNewHomes}
                onValueChange={handleFilterChange('excludeNewHomes')}
                containerStyle={{ marginBottom: 10 }}
              />
            )}
          </FilterPaneContent>

          <FilterPaneBottom>
            <Button
              color={theme.white}
              fontColor={theme.textDark}
              containerStyle={{ border: `1px solid ${theme.textDark}` }}
              onClick={handleClearFilters}
              type="button"
            >
              Clear Filters
            </Button>
            <Button type="submit">Apply Changes</Button>
          </FilterPaneBottom>
        </FilterPane>
      </Content>
      {closeToModal && (
        <CloseToSearchModal
          show={closeToModal}
          onClose={onCloseCloseToModal}
          onCloseToSearch={props.onCloseToSearch}
          polyIds={filters?.polyIds}
        />
      )}
    </Container>
  );
};

export default SearchBar;
